import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Umbrella, Stethoscope, Trash2, Filter, X, Check, Clock, Ban, AlertTriangle } from 'lucide-react';
import { AUSENCIA_LABEL, AUSENCIA_STYLE } from '../../lib/labels';
import VacacionesSaldosCard from './VacacionesSaldosCard';

const ESTADO_STYLE = {
  pendiente: 'bg-amber-100 text-amber-800 border border-amber-200',
  aprobada: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  rechazada: 'bg-red-100 text-red-800 border border-red-200',
};
const ESTADO_LABEL = { pendiente: 'Pendiente', aprobada: 'Aprobada', rechazada: 'Rechazada' };

const AusenciasTab = () => {
  const [ausencias, setAusencias] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [na, setNa] = useState({ user_id: '', tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });

  // Filtros
  const [fEmpleado, setFEmpleado] = useState('all');
  const [fTipo, setFTipo] = useState('all');
  const [fEstado, setFEstado] = useState('all');
  const [fDesde, setFDesde] = useState('');
  const [fHasta, setFHasta] = useState('');

  // Diálogo aprobar/rechazar
  const [dialogo, setDialogo] = useState({ open: false, ausencia: null, accion: 'aprobar', comentario: '' });

  const cargarAusencias = () => api.get('/admin/ausencias').then((r) => setAusencias(r.data)).catch(() => {});
  const cargarEmpleados = () => api.get('/admin/empleados').then((r) => setEmpleados(r.data)).catch(() => {});
  useEffect(() => { cargarAusencias(); cargarEmpleados(); }, []);

  const crearAusencia = async (e) => {
    e.preventDefault();
    if (!na.user_id) { toast.error('Selecciona un empleado'); return; }
    if (na.fecha_fin < na.fecha_inicio) { toast.error('La fecha "Hasta" no puede ser anterior a "Desde"'); return; }
    try {
      await api.post('/admin/ausencias', na);
      toast.success('Ausencia registrada');
      setNa({ user_id: '', tipo: 'vacaciones', fecha_inicio: '', fecha_fin: '', motivo: '' });
      cargarAusencias();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };
  const borrarAusencia = async (id) => {
    if (!window.confirm('¿Eliminar esta ausencia?')) return;
    try { await api.delete(`/admin/ausencias/${id}`); cargarAusencias(); } catch { toast.error('Error'); }
  };

  const abrirDialogo = (ausencia, accion) => setDialogo({ open: true, ausencia, accion, comentario: '' });
  const cerrarDialogo = () => setDialogo({ open: false, ausencia: null, accion: 'aprobar', comentario: '' });

  const confirmarDecision = async () => {
    if (!dialogo.ausencia) return;
    try {
      await api.post(`/admin/ausencias/${dialogo.ausencia.id}/${dialogo.accion}`, { comentario: dialogo.comentario });
      toast.success(dialogo.accion === 'aprobar' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      cerrarDialogo();
      cargarAusencias();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const limpiarFiltros = () => { setFEmpleado('all'); setFTipo('all'); setFEstado('all'); setFDesde(''); setFHasta(''); };

  const ausenciasFiltradas = useMemo(() => {
    return ausencias.filter((a) => {
      if (fEmpleado !== 'all' && a.user_id !== fEmpleado) return false;
      if (fTipo !== 'all' && a.tipo !== fTipo) return false;
      if (fEstado !== 'all' && (a.estado || 'aprobada') !== fEstado) return false;
      if (fDesde && a.fecha_fin < fDesde) return false;
      if (fHasta && a.fecha_inicio > fHasta) return false;
      return true;
    });
  }, [ausencias, fEmpleado, fTipo, fEstado, fDesde, fHasta]);

  const pendientesCount = useMemo(() => ausencias.filter((a) => a.estado === 'pendiente').length, [ausencias]);
  const filtrosActivos = fEmpleado !== 'all' || fTipo !== 'all' || fEstado !== 'all' || !!fDesde || !!fHasta;

  return (
    <div className="space-y-6" data-testid="ausencias-tab">
      <VacacionesSaldosCard />
      {pendientesCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-4 py-3 flex items-center gap-3" data-testid="pendientes-banner">
          <Clock className="h-5 w-5 text-amber-700" />
          <div className="text-sm">
            <strong>{pendientesCount}</strong> solicitud{pendientesCount === 1 ? '' : 'es'} pendiente{pendientesCount === 1 ? '' : 's'} de revisión.
          </div>
          <Button size="sm" variant="outline" className="ml-auto" onClick={() => setFEstado('pendiente')} data-testid="ver-pendientes-btn">
            Ver solo pendientes →
          </Button>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Formulario nueva ausencia (registro directo del admin) */}
        <Card className="border-zinc-200 h-fit">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <Umbrella className="h-5 w-5" /> Nueva ausencia
            </CardTitle>
            <p className="text-xs text-zinc-500 mt-1">Registro directo (queda aprobada). Los empleados también pueden solicitar desde la app.</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={crearAusencia} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Empleado</Label>
                <Select value={na.user_id} onValueChange={(v) => setNa({ ...na, user_id: v })}>
                  <SelectTrigger data-testid="new-ausencia-empleado"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>
                    {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={na.tipo} onValueChange={(v) => setNa({ ...na, tipo: v })}>
                  <SelectTrigger data-testid="new-ausencia-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(AUSENCIA_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Desde</Label>
                  <Input type="date" value={na.fecha_inicio} onChange={(e) => setNa({ ...na, fecha_inicio: e.target.value })} required data-testid="new-ausencia-inicio" />
                </div>
                <div className="space-y-1.5">
                  <Label>Hasta</Label>
                  <Input type="date" value={na.fecha_fin} onChange={(e) => setNa({ ...na, fecha_fin: e.target.value })} required data-testid="new-ausencia-fin" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Motivo (opcional)</Label>
                <Input value={na.motivo} onChange={(e) => setNa({ ...na, motivo: e.target.value })} data-testid="new-ausencia-motivo" />
              </div>
              <Button type="submit" className="w-full bg-zinc-900 hover:bg-zinc-800" data-testid="create-ausencia-btn">Registrar ausencia</Button>
            </form>
          </CardContent>
        </Card>

        {/* Listado + filtros */}
        <Card className="border-zinc-200 lg:col-span-2">
          <CardHeader className="space-y-4">
            <CardTitle className="font-heading flex items-center gap-2 text-lg">
              <Stethoscope className="h-5 w-5" />
              Ausencias, vacaciones y bajas
              <Badge variant="outline" className="ml-1" data-testid="ausencias-count">
                {ausenciasFiltradas.length}{filtrosActivos && ausenciasFiltradas.length !== ausencias.length ? ` / ${ausencias.length}` : ''}
              </Badge>
            </CardTitle>

            {/* Filtros */}
            <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-3 space-y-3" data-testid="ausencias-filtros">
              <div className="flex items-center gap-2 text-xs font-semibold text-zinc-600 uppercase tracking-wider">
                <Filter className="h-3.5 w-3.5" /> Filtros
                {filtrosActivos && (
                  <Button variant="ghost" size="sm" className="ml-auto h-7 text-xs" onClick={limpiarFiltros} data-testid="ausencias-filtros-limpiar">
                    <X className="h-3 w-3 mr-1" /> Limpiar
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Estado</Label>
                  <Select value={fEstado} onValueChange={setFEstado}>
                    <SelectTrigger data-testid="filtro-estado" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pendiente">Pendiente{pendientesCount > 0 ? ` (${pendientesCount})` : ''}</SelectItem>
                      <SelectItem value="aprobada">Aprobada</SelectItem>
                      <SelectItem value="rechazada">Rechazada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Empleado</Label>
                  <Select value={fEmpleado} onValueChange={setFEmpleado}>
                    <SelectTrigger data-testid="filtro-empleado" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {empleados.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={fTipo} onValueChange={setFTipo}>
                    <SelectTrigger data-testid="filtro-tipo" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {Object.entries(AUSENCIA_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Desde</Label>
                  <Input type="date" value={fDesde} onChange={(e) => setFDesde(e.target.value)} data-testid="filtro-desde" className="h-9" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hasta</Label>
                  <Input type="date" value={fHasta} onChange={(e) => setFHasta(e.target.value)} data-testid="filtro-hasta" className="h-9" />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto max-h-[32rem] overflow-y-auto">
              <table className="w-full" data-testid="ausencias-table">
                <thead className="bg-zinc-50 sticky top-0">
                  <tr className="text-left text-xs font-medium text-zinc-500 uppercase tracking-wider">
                    <th className="py-3 px-4">Empleado</th>
                    <th className="py-3 px-4">Estado</th>
                    <th className="py-3 px-4">Tipo</th>
                    <th className="py-3 px-4">Periodo</th>
                    <th className="py-3 px-4">Motivo</th>
                    <th className="py-3 px-4 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {ausenciasFiltradas.map((a) => {
                    const estado = a.estado || 'aprobada';
                    return (
                      <tr key={a.id} className={`border-t border-zinc-100 ${estado === 'pendiente' ? 'bg-amber-50/40' : ''}`} data-testid={`ausencia-row-${a.id}`}>
                        <td className="py-2.5 px-4 font-medium text-zinc-900">
                          {a.full_name}
                          {a.solicitado_por && a.solicitado_por !== 'admin' && (
                            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mt-0.5">
                              solicitada por {a.solicitado_por === 'empapp' ? 'app' : 'kiosko'}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-4">
                          <Badge className={ESTADO_STYLE[estado]}>{ESTADO_LABEL[estado]}</Badge>
                        </td>
                        <td className="py-2.5 px-4"><Badge className={AUSENCIA_STYLE[a.tipo]}>{AUSENCIA_LABEL[a.tipo]}</Badge></td>
                        <td className="py-2.5 px-4 tabular-nums text-sm">
                          {new Date(a.fecha_inicio).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })} – {new Date(a.fecha_fin).toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid' })}
                        </td>
                        <td className="py-2.5 px-4 text-sm text-zinc-500 max-w-xs truncate">
                          {a.motivo || '-'}
                          {a.comentario_admin && (
                            <div className="text-xs text-zinc-400 mt-0.5">
                              <span className="font-medium">Nota admin:</span> {a.comentario_admin}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-4 text-right whitespace-nowrap">
                          {estado === 'pendiente' ? (
                            <div className="inline-flex gap-1.5">
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-8" onClick={() => abrirDialogo(a, 'aprobar')} data-testid={`aprobar-ausencia-${a.id}`}>
                                <Check className="h-3.5 w-3.5 mr-1" /> Aprobar
                              </Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 h-8" onClick={() => abrirDialogo(a, 'rechazar')} data-testid={`rechazar-ausencia-${a.id}`}>
                                <Ban className="h-3.5 w-3.5 mr-1" /> Rechazar
                              </Button>
                            </div>
                          ) : (
                            <Button size="icon" variant="outline" className="text-red-600" onClick={() => borrarAusencia(a.id)} data-testid={`delete-ausencia-${a.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {ausenciasFiltradas.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-zinc-400 text-sm">
                        {filtrosActivos ? 'Sin resultados con los filtros aplicados.' : 'Sin ausencias registradas.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Diálogo aprobar / rechazar */}
      <Dialog open={dialogo.open} onOpenChange={(o) => !o && cerrarDialogo()}>
        <DialogContent data-testid="dialogo-decision">
          <DialogHeader>
            <DialogTitle>
              {dialogo.accion === 'aprobar' ? 'Aprobar solicitud' : 'Rechazar solicitud'}
            </DialogTitle>
          </DialogHeader>
          {dialogo.ausencia && (
            <div className="space-y-3 text-sm">
              <div className="bg-zinc-50 rounded-lg p-3 space-y-1">
                <div><span className="text-zinc-500">Empleado:</span> <strong>{dialogo.ausencia.full_name}</strong></div>
                <div><span className="text-zinc-500">Tipo:</span> {AUSENCIA_LABEL[dialogo.ausencia.tipo]}</div>
                <div><span className="text-zinc-500">Periodo:</span> {dialogo.ausencia.fecha_inicio} → {dialogo.ausencia.fecha_fin}</div>
                {dialogo.ausencia.motivo && <div><span className="text-zinc-500">Motivo:</span> {dialogo.ausencia.motivo}</div>}
                {dialogo.ausencia.tipo === 'vacaciones' && (
                  <div className="mt-2 pt-2 border-t border-zinc-200">
                    <div><span className="text-zinc-500">Días que se descontarán:</span> <strong data-testid="dialogo-dias-calculados">{dialogo.ausencia.dias_calculados || 0}</strong></div>
                    {dialogo.ausencia.excede_saldo && dialogo.accion === 'aprobar' && (
                      <div className="mt-2 flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 text-xs" data-testid="dialogo-excede-saldo">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                        <span>La solicitud <strong>excede el saldo disponible</strong> del empleado. Puedes aprobar igualmente (quedará en negativo) o rechazar.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Comentario (opcional, se envía al empleado por email)</Label>
                <Textarea
                  rows={3}
                  value={dialogo.comentario}
                  onChange={(e) => setDialogo({ ...dialogo, comentario: e.target.value })}
                  placeholder={dialogo.accion === 'aprobar' ? '¡Disfruta!' : 'Ese periodo choca con otro compañero, ¿podemos cambiar la fecha?'}
                  data-testid="dialogo-comentario"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={cerrarDialogo}>Cancelar</Button>
            <Button
              className={dialogo.accion === 'aprobar' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={confirmarDecision}
              data-testid="dialogo-confirmar"
            >
              {dialogo.accion === 'aprobar' ? 'Aprobar' : 'Rechazar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AusenciasTab;
